import { CommonModule } from '@angular/common';
import { BrisbaneDatePipe } from '../../../pipes/brisbane-date.pipe';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface EventManagerFeedbackDialogData {
  eventId: string;
  feedback: string;
  eventManagerName: string;
  canEdit: boolean;
  updatedAt?: string | Date | null;
  updatedByName?: string;
}

@Component({
  selector: 'app-event-manager-feedback-dialog',
  standalone: true,
  templateUrl: './event-manager-feedback-dialog.component.html',
  styleUrls: ['./event-manager-feedback-dialog.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, BrisbaneDatePipe],
})
export class EventManagerFeedbackDialogComponent {
  form: FormGroup;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<EventManagerFeedbackDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EventManagerFeedbackDialogData,
  ) {
    this.form = this.fb.group({
      feedback: [
        data.feedback || '',
        [Validators.maxLength(100)],
      ],
    });
  }

  get remainingCharacters(): number {
    return 100 - (this.form.get('feedback')?.value?.length || 0);
  }

  get isReadOnly(): boolean {
    return !this.data.canEdit;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    if (this.isReadOnly) {
      this.dialogRef.close(false);
      return;
    }

    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = {
      feedback: this.form.get('feedback')?.value ?? '',
    };

    this.httpService
      .put<any>(`events/${this.data.eventId}/manager-feedback`, payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.showSuccessToast(response.message || 'Feedback saved successfully.');
            this.dialogRef.close({ success: true });
            return;
          }

          this.showErrorToast(response?.message || 'Failed to save feedback.');
        },
        error: (error) => {
          this.showErrorToast(
            error?.message || 'Something went wrong while saving feedback.',
          );
        },
      });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-toast'],
    });
  }

  private showErrorToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'error' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-toast'],
    });
  }
}
